import {assert} from 'assert-ts';
import {Concept} from 'types';
import type {
  AnyValidation,
  Condition,
  Part,
  PartSchema,
  RoleEntityCode,
  SelectableThesaurusNodeType,
  TypedSchema,
  TypedSchemaModifierMap,
} from 'schemaDefinition/types';
import {Separator} from 'schemaDefinition/types';
import type {WorkImportProps, WorkV4} from 'services/metadata/types';
import type {MetadataModifierKey} from '../types';
import {StatusModifierKey, WorkBook, WorkMerchandise} from '../types';
import {isFeatureEnabled} from '../../configuration';
import {GenreAndFormRef} from '../references';
import {CodeListRef} from '../references/CodeListRef';
import {ThemaRef} from '../references/ThemaRef';
import {grepYearLevelToEducationLevelMap} from '../valueModifiers/mapper/educationLevelToGrepYearLevel';
import {
  isUnnamedAgentLink,
  validateExternalLinkedAgentId,
  validateLinkedAgentId,
} from './partAgentsValidations';

type WorkSchema = TypedSchema<WorkV4, WorkImportProps>;

type WorkCondition<TName extends keyof WorkV4> = Condition<
  WorkV4,
  WorkV4,
  WorkV4[TName],
  WorkImportProps
>;

type WorkValidation<TName extends keyof WorkV4> = AnyValidation<
  WorkV4,
  WorkV4,
  WorkV4[TName],
  WorkImportProps
>;

type WorkPart = Part<WorkV4, WorkV4, WorkImportProps>;

type WorkPartSchema = PartSchema<WorkV4, WorkV4, WorkImportProps>;

/**
 * WorkWithExpressions schema
 */

const isBook: WorkCondition<'type'> = {
  regex: CodeListRef.WORK_TYPE['Bok'],
  arg: {
    $ref: ':type',
  },
  default: true, // BOOK is default although type is required
};

const isSchoolBook: WorkCondition<'literatureType'> = {
  regex: CodeListRef.LITERATURE_TYPE['Skolebøker'],
  arg: {
    $ref: ':literatureType',
  },
  default: false,
};

const isNotSchoolBook: WorkCondition<'literatureType'> = {
  $not: isSchoolBook,
};

const litteraturType_includesSchoolbook: WorkCondition<'literatureType'> = {
  $includes: {
    arg1: {$ref: ':literatureType'},
    arg2: CodeListRef.LITERATURE_TYPE['Schoolbooks'],
  },
  default: false,
};

const litteraturType_includesLærebok: WorkCondition<'literatureType'> = {
  $includes: {
    arg1: {$ref: ':literatureType'},
    arg2: CodeListRef.LITERATURE_TYPE['Lærebøker (høyere utd.)'],
  },
  default: false,
};

const isSkjønnlitteratur: WorkCondition<'literatureType'> = {
  $includes: {
    arg1: {$ref: ':literatureType'},
    arg2: CodeListRef.LITERATURE_TYPE['Skjønnlitteratur'],
  },
  default: false,
};

export const deweyRegex = [
  // Just 3 digits
  '^[0-9]{3}$',
  // Or more digits with .
  '^[0-9]{3}\\.[0-9]+$',
  // Or more digits with /.
  '^[0-9]{3}/\\.[0-9]+$',
  // Or more digits with .--/--
  '^[0-9]{3}\\.[0-9]+/[0-9]+$',
];
const deweyValueFormat: WorkValidation<'dewey'> = {
  regex: deweyRegex,
  default: true,
};

const deweyExternalValueAndDeweyValueMatch: WorkValidation<'dewey'> = {
  $or: [
    // Ignore if externalDewey is empty
    {$eq: [{$ref: '/externalDewey.length'}, 0]},
    // First value must match first value of externalDewey
    {$eq: [{$ref: '/dewey[0].value'}, {$ref: '/externalDewey[0].value'}]},
  ],
  level: 'warning',
  messageKey: 'deweyValueMustMatchFirstExternalDeweyValue',
  default: true,
};

const isIntellectualLevel_children: WorkCondition<'intellectualLevel'> = {
  $includes: {
    arg1: {$ref: ':intellectualLevel'},
    arg2: [
      CodeListRef.INTELLECTUAL_LEVEL['0-3'],
      CodeListRef.INTELLECTUAL_LEVEL['3-6'],
      CodeListRef.INTELLECTUAL_LEVEL['6-9'],
      CodeListRef.INTELLECTUAL_LEVEL['9-12'],
      CodeListRef.INTELLECTUAL_LEVEL['12-16'],
      CodeListRef.INTELLECTUAL_LEVEL['16-20'],
    ],
  },
  default: false,
};

const emptyNorwegianSubject: WorkCondition<'norwegianSubjects'> = {
  $not: {arg: {$ref: ':norwegianSubjects'}},
};

const emptyGenreAndForm: WorkCondition<'genreAndForm'> = {
  $not: {arg: {$ref: ':genreAndForm'}},
};

const genreAndForm_includesLærebok: WorkCondition<'genreAndForm'> = {
  $includes: {
    arg1: {$ref: ':genreAndForm'},
    arg2: GenreAndFormRef['Lærebøker'],
  },
  default: false,
};

const genreAndForm_includesBiography: WorkCondition<'genreAndForm'> = {
  $includes: {
    arg1: {$ref: ':genreAndForm'},
    arg2: GenreAndFormRef['Biographies'],
  },
  default: false,
};

const mentionsAgent: RoleEntityCode = `mentions.${Concept.agent}`;
const links_includesMentionsAgent: WorkCondition<'links'> = {
  regex: `"${mentionsAgent}"`,
  default: true,
};

const thema_includesHøyereUtdanning: WorkCondition<'themes'> = {
  regex: `${ThemaRef['Høyere utdanning']}.*`,
  arg: {
    $ref: ':themes',
  },
  default: false,
};

const thema_includes4C: WorkCondition<'themes'> = {
  regex: `${ThemaRef['For alle utdanningsnivå']}.*`,
  arg: {
    $ref: ':themes',
  },
  default: false,
};

const thema_includes4Z: WorkCondition<'themes'> = {
  regex: `${ThemaRef['For spesifikke nasjonale eller regionale læreplaner']}.*`,
  arg: {
    $ref: ':themes',
  },
  default: false,
};

const thema_includesAge: WorkCondition<'themes'> = {
  regex: `${ThemaRef['Intellektuelt nivå']}.*`,
  arg: {
    $ref: ':themes',
  },
  default: false,
};

/**
 *  Either litteraturtype is not lærebok or thema includes høyere utdanning
 */
const themes_validation_litteraturType_higherEducation: WorkValidation<'themes'> =
  {
    $or: [
      {
        $not: litteraturType_includesLærebok,
      },
      thema_includesHøyereUtdanning,
    ],
    level: 'warning',
    messageKey: 'themes.lærebok.mismatch',
  };

/**
 *  Either litteraturtype is not skolebok or thema includes 4C* and 4Z*
 */
const themes_validation_litteraturType_schoolbook: WorkValidation<'themes'> = {
  $or: [
    {
      $not: litteraturType_includesSchoolbook,
    },
    {
      $and: [thema_includes4C, thema_includes4Z],
    },
  ],
  level: 'warning',
  messageKey: 'themes.skolebok.mismatch',
};

/**
 * Codes from the same hierarchy are not allowed together.
 * FB cannot be with FBA. But FBA and FBB is ok.
 */
export const themes_validation_noSameHierarchyCodes: WorkValidation<'themes'> =
  {
    $not: {
      regex: ['"([A-Z0-9]+)".*"\\1[A-Z0-9]', '"([A-Z0-9]+)[A-Z0-9]+".*"\\1"'],
      default: false,
    },
    messageKey: 'themes.hierarchy.mismatch',
  };

const theme_validation_intellectualLevel_children: WorkValidation<'themes'> = {
  $or: [
    {
      $not: isIntellectualLevel_children,
    },
    thema_includesAge,
  ],
  level: 'warning',
  messageKey: 'themes.intellectualLevel.mismatch',
};

// Source must be equal to [0].source
const deweySourceMustMatchFirstDeweySource: AnyValidation = {
  $eq: {$ref: '/dewey[0].source'}, // Must use global path, since we are inside a sub-schema
};

const deweySourceShouldNotBeOutdated: AnyValidation = {
  level: 'warning',
  messageKey: 'deweySource.isOutdated',
  $or: [
    {$eq: CodeListRef.DEWEY_SOURCE['23/nor']},
    {$eq: CodeListRef.DEWEY_SOURCE['23/eng']},
  ],
};

export const deweyPart: WorkPartSchema = {
  type: 'schema',
  name: 'dewey',
  cardinality: '2',
  compare: 'items',
  parts: [
    [
      {
        type: 'text',
        name: 'value',
        key: 'deweyNumber',
        required: true,
        validation: [deweyValueFormat],
      },
      {
        type: 'codelist',
        name: 'source',
        key: 'deweySource',
        codelistId: 'DEWEY_SOURCE',
        required: true,
        default: CodeListRef.DEWEY_SOURCE['23/nor'],
        validation: [
          // Source must be equal to [0].source
          deweySourceMustMatchFirstDeweySource,
        ],
      },
    ],
  ],
};

const externalDeweyPart: WorkPartSchema = {
  type: 'schema',
  name: 'externalDewey',
  cardinality: 'multiple',
  readonly: true,
  noValidation: true,
  parts: [
    [
      {
        type: 'text',
        name: 'value',
        key: 'deweyNumber',
        actions: ['copy'],
      },
      {
        type: 'codelist',
        name: 'source',
        key: 'deweySource',
        codelistId: 'DEWEY_SOURCE',
      },
      {
        type: 'text',
        name: 'origin',
        key: 'deweyOrigin',
      },
    ],
  ],
};

export const intellectualLevelPart: WorkPart = {
  type: 'codelist',
  name: 'intellectualLevel',
  key: 'intellectualLevel',
  cardinality: 'multiple',
  codelistId: 'INTELLECTUAL_LEVEL',
};

/** YP* => 4C*, or equivalent: (!YP* or 4C*) */
export const workValidation_YcodeRequires5Acode: WorkValidation<'themes'> = {
  id: 'YPcodeRequires4Ccode',
  messageKey: 'YPcodeRequires4Ccode',
  $or: [
    {
      $not: {
        regex: '"YP.*"',
        default: false,
      },
    },
    {
      regex: '"4C.*"',
      default: false,
    },
  ],
};

/**
 * For each education level with a corresponding grep year level, grep should
 * include that year level. Education levels without a mapping are not validated.
 */
const educationLevel_grepYearLevelValidations: WorkValidation<'educationLevel'>[] =
  Object.entries(grepYearLevelToEducationLevelMap).map(
    ([grepYearLevel, educationLevel]) => ({
      $or: [
        {$not: {$eq: educationLevel}},
        {
          $includes: {
            arg1: {$ref: ':grep'},
            arg2: grepYearLevel,
          },
          default: false,
        },
      ],
      level: 'warning',
      messageKey: 'educationLevel.grepYearLevel.missing',
    }),
  );

const selectableGrepNodeTypes: SelectableThesaurusNodeType[] = [
  'education_program_type',
  'education_program',
  'program_area',
  'subject_code',
];

if (isFeatureEnabled('grepLevel')) {
  selectableGrepNodeTypes.push('year_level');
}

/**
 * Warn if book has two or more preferred titles and title is not used as main entry:
 * Either not book, less than two preferred titles, or title used as main entry
 */
const useTitleAsMainEntry_validation_multiplePreferredTitles: WorkValidation<'useTitleAsMainEntry'> =
  {
    $or: [
      {$not: isBook},
      {$not: {$ge: [{$ref: ':preferredTitle.length'}, 2]}},
      {$eq: true},
    ],
    level: 'warning',
    messageKey: 'useTitleAsMainEntry.multiplePreferredTitles',
  };

export const workDataSchema: WorkSchema = {
  name: 'work',
  parts: [
    [
      {
        type: 'codelist',
        name: 'type',
        required: true,
        codelistId: 'WORK_TYPE',
      },
      {
        type: 'year',
        name: 'firstPublishedYear',
      },
    ],
    {
      type: 'linkedAgent',
      name: 'agents',
      cardinality: 'multiple',
      roleCodelistId: 'WORK_ROLE_TYPE',
      entitySubtypes: [Concept.person, Concept.corporation, Concept.event],
      compare: 'subValues',
    },
    Separator('card', 'line'),
    {
      type: 'expand',
      role: 'showTitleAsMainEntryIfBook',
      when: [
        {
          condition: isBook,
          parts: [
            {
              type: 'bool',
              name: 'useTitleAsMainEntry',
              key: 'titleAsMainEntry',
            },
          ],
        },
      ],
    },
    {
      type: 'text',
      name: 'preferredTitle',
      key: 'preferredTitle',
      cardinality: 'multiple',
      compare: 'items',
    },
    {
      type: 'schema',
      name: 'otherTitles',
      parts: [
        [
          {
            type: 'text',
            name: 'value',
            key: 'otherTitle.value',
            required: true,
          },
          {
            type: 'codelist',
            name: 'type',
            key: 'otherTitle.type',
            codelistId: 'WORK_TITLE_TYPE',
            required: true,
          },
        ],
      ],
      cardinality: 'multiple',
    },
    {
      type: 'codelist',
      name: 'languages',
      key: 'language',
      codelistId: 'LANGUAGE',
      cardinality: 'multiple',
    },
    Separator('card', 'line'),
    // Prizes
    {
      type: 'schema',
      name: 'prizes',
      parts: [
        [
          {
            name: 'year',
            type: 'year',
          },
          {
            name: 'type',
            type: 'codelist',
            codelistId: 'PRIZE_TYPE',
          },
          {
            type: 'linkedAgent',
            name: 'agent',
            cardinality: 'single',
            entityRole: 'dummy',
            entitySubtypes: [Concept.person],
            roleCodelistId: 'DUMMY',
          },
          {
            type: 'linkedLiterary',
            name: 'prize',
            roleCodelistId: 'work.linkedRole',
            cardinality: 'single',
            linkRole: 'awarded.prize',
          },
        ],
      ],
      cardinality: 'multiple',
      readonly: true,
      noValidation: true,
    },
    {
      type: 'linkedLiterary',
      name: 'links',
      key: 'links',
      cardinality: 'multiple',
      //linkPropRequired: 'should',
      roleCodelistId: 'work.linkedRole',
      compare: 'items',
    },
    Separator('card', 'line'),
    {
      type: 'expand',
      role: 'showIfBook',
      when: [
        {
          condition: isBook,
          parts: [
            {
              type: 'codelist',
              name: 'literatureType',
              cardinality: 'multiple',
              codelistId: 'LITERATURE_TYPE',
              key: 'literatureType',
              listValidation: {
                // Hvis litteraturtype er Lærebøker (høyere utd.), så må også faglitteratur være satt:
                // ('Faglitteratur' or not 'Lærebøker (høyere utd.)')
                $or: [
                  {
                    regex: CodeListRef.LITERATURE_TYPE['Faglitteratur'],
                    default: false,
                  },
                  {
                    $not: {
                      regex:
                        CodeListRef.LITERATURE_TYPE['Lærebøker (høyere utd.)'],
                      default: false,
                    },
                  },
                ],
                messageKey: 'literatureType.LærebøkerRequiresFaglitteratur',
              },
            },
            deweyPart,
            externalDeweyPart,
          ],
        },
      ],
    },
    {
      // Vis trinn og målgruppe når litteraturtype er skolebøker
      type: 'expand',
      role: 'showIntellektueltNivaaEllerMaalgruppe',
      when: [
        {
          condition: isSchoolBook,
          parts: [
            {
              type: 'codelist',
              name: 'educationTargetGroup',
              codelistId: 'EDUCATION_TARGET_GROUP',
              cardinality: 'multiple',
            },
          ],
        },
      ],
      default: [intellectualLevelPart],
    },
    {
      type: 'expand',
      role: 'showBibleIfBook',
      when: [
        {
          condition: isBook,
          parts: [
            Separator('card', 'line'),
            {
              type: 'codelist',
              name: 'bibleVersion',
              codelistId: 'BIBLE_VERSION',
            },
            {
              type: 'codelist',
              name: 'bibleContent',
              codelistId: 'BIBLE_CONTENT',
              cardinality: 'multiple',
            },
          ],
        },
      ],
    },
    {
      type: 'expand',
      role: 'showAdultImmigrantsIfNotSchoolBook',
      when: [
        {
          condition: [isBook, isNotSchoolBook],
          parts: [
            Separator('card', 'line'),
            {
              type: 'bool',
              name: 'norwegianForAdultImmigrants',
              key: 'norwegianForAdultImmigrants',
            },
          ],
        },
      ],
    },
    {
      type: 'expand',
      role: 'visSkoleTrinnOgUndervisningsMateriellForSkolebøker',
      when: [
        {
          condition: isSchoolBook,
          parts: [
            {
              type: 'codelist',
              name: 'educationLevel',
              codelistId: 'EDUCATION_LEVEL',
              cardinality: 'multiple',
              validation: educationLevel_grepYearLevelValidations,
            },
            {
              type: 'codelist',
              name: 'teachingMaterials',
              codelistId: 'TEACHING_MATERIALS',
              cardinality: 'multiple',
            },
          ],
        },
      ],
    },
    {
      type: 'expand',
      role: 'showGrepIfBookAndSchoolbook',
      when: [
        {
          condition: [isBook, isSchoolBook],
          parts: [
            Separator('card', 'line'),
            {
              key: 'grep',
              type: 'thesaurus',
              name: 'grep',
              cardinality: 'multiple',
              thesaurusId: 'grep',
              showCode: true,
              selectableNodeTypes: selectableGrepNodeTypes,
              variant: 'byType',
            },
          ],
        },
      ],
    },
    Separator('card', 'line'),
    {
      type: 'codelist',
      name: 'bookGroup',
      codelistId: 'WORK_BOOK_GROUP',
      showCode: true,
    },
    Separator('card', 'line'),
    {
      type: 'expand',
      role: 'showGrepIfBookAndSchoolbook',
      when: [
        {
          // When book and not schoolbook: Multi-edit thesaurus genreAndForm, subject and themes
          condition: [isBook, isNotSchoolBook],
          parts: [
            {
              type: 'action',
              role: 'multiThesaurusSchoolbook',
              actionName: 'multiThesaurus',
              partNames: isFeatureEnabled('subjects')
                ? ['genreAndForm', 'subjects', 'themes', 'norwegianSubjects']
                : ['genreAndForm', 'norwegianSubjects', 'themes'],
            },
          ],
        },
      ],
      // Default: multi-edit thesaurus for genreAndForm and themes only
      default: [
        {
          type: 'action',
          role: 'multiThesaurusNonSchoolbook',
          actionName: 'multiThesaurus',
          partNames: ['genreAndForm', 'themes'],
        },
      ],
    },
    {
      type: 'thesaurus',
      name: 'genreAndForm',
      cardinality: 'multiple',
      thesaurusId: 'genreandform',
      showCode: false,
      validation: {
        // Either litteraturtype is not lærebok or genreAndForm includes lærebok
        $or: [
          {
            $not: litteraturType_includesLærebok,
          },
          genreAndForm_includesLærebok,
        ],
        level: 'warning',
        messageKey: 'genreAndForm.lærebok.mismatch',
      },
    },
    {
      type: 'expand',
      role: 'showSubjectIfBookAndNotSchoolbook',
      when: [
        {
          condition: [isBook, isNotSchoolBook],
          parts: [
            Separator('card', 'line'),
            {
              type: 'thesaurus',
              name: 'norwegianSubjects',
              cardinality: 'multiple',
              thesaurusId: 'neo',
              showCode: false,
            },
            ...((isFeatureEnabled('subjects')
              ? [
                  {
                    type: 'thesaurus',
                    name: 'subjects',
                    cardinality: 'multiple',
                    thesaurusId: 'bokbasen',
                    showCode: false,
                  },
                ]
              : []) as WorkSchema['parts']),
          ],
        },
      ],
    },
    Separator('card', 'line'),
    {
      type: 'thesaurus',
      name: 'themes',
      cardinality: 'multiple',
      thesaurusId: 'thema',
      showCode: true,
      listValidation: [
        themes_validation_litteraturType_higherEducation,
        themes_validation_litteraturType_schoolbook,
        themes_validation_noSameHierarchyCodes,
      ],
    },
    Separator('card', 'line'),
    {
      type: 'text',
      name: 'generalNote',
      key: 'generalNote',
      cardinality: 'multiple',
    },
    {
      type: 'textarea',
      name: 'noteForCataloging',
      key: 'noteForCataloging',
      readonly: true,
    },
  ],
};

export const workSchemaModifiers: TypedSchemaModifierMap<
  WorkV4,
  MetadataModifierKey
> = {
  [`${WorkMerchandise}.${StatusModifierKey.new}`]: [
    {
      // Required by backend so temporarily required here
      name: 'preferredTitle',
      required: true,
    },
  ],
  [`${WorkMerchandise}.${StatusModifierKey.precat}`]: [
    {
      name: 'languages',
      required: 'should',
    },
  ],
  [`${WorkMerchandise}.${StatusModifierKey.cat}`]: [
    {
      name: 'firstPublishedYear',
      required: 'should',
    },
    {
      name: 'languages',
      required: true,
    },
  ],
  [`${WorkBook}.${StatusModifierKey.new}`]: [
    {
      name: 'preferredTitle',
      required: true,
    },
  ],
  [`${WorkBook}.${StatusModifierKey.precat}`]: [
    {
      name: 'languages',
      required: 'should',
    },
    {
      name: 'literatureType',
      required: true,
    },
    {
      name: 'dewey',
      validation: [deweyValueFormat],
      listValidation: [deweyExternalValueAndDeweyValueMatch],
      parts: [
        {
          name: 'source',
          validation: [deweySourceShouldNotBeOutdated],
        },
      ],
    },
    {
      name: 'intellectualLevel',
      required: 'should',
    },
    {
      name: 'genreAndForm',
      required: {
        should: {
          $or: [emptyNorwegianSubject, isSkjønnlitteratur],
        },
      },
    },
    {
      name: 'norwegianSubjects',
      required: {
        should: emptyGenreAndForm,
      },
    },
    {
      name: 'themes',
      required: 'should',
      listValidation: [theme_validation_intellectualLevel_children],
    },
  ],
  [`${WorkBook}.${StatusModifierKey.cat}`]: [
    {
      name: 'firstPublishedYear',
      required: 'should',
    },
    {
      name: 'useTitleAsMainEntry',
      validation: useTitleAsMainEntry_validation_multiplePreferredTitles,
    },
    {
      name: 'languages',
      required: true,
      listValidation: {
        level: 'warning',
        $not: {
          $eq: [{$ref: ':languages'}, [CodeListRef.LANGUAGE.Flerspråklig]],
        },
        messageKey: 'languages.not.only.multilingual',
        default: true,
      },
    },
    {
      name: 'teachingMaterials',
      required: 'should',
    },
    {
      name: 'dewey',
      required: 'should',
      validation: [deweyValueFormat],
      listValidation: [deweyExternalValueAndDeweyValueMatch],
    },
    {
      name: 'intellectualLevel',
      required: true,
    },
    {
      name: 'links',
      validation: {
        // Valdiation will only trigger for links with languages, i.e. series,
        // where one of the languages of the series must match the language of the work
        $includes: {
          arg1: {$ref: '.link.languages'},
          arg2: {$ref: ':languages[0]'},
        },
        default: true,
        messageKey: 'link.series.languageMismatch',
        level: 'warning',
      },
      required: {
        should: {
          $or: [
            {
              $not: links_includesMentionsAgent,
            },
            genreAndForm_includesBiography,
          ],
        },
      },
      listValidation: {
        // When genreAndForm includes biography, then links must include mentioned person
        messageKey: 'link.biographyMismatch',
        level: 'warning',
        $or: [
          {
            $not: genreAndForm_includesBiography,
          },
          links_includesMentionsAgent,
        ],
      },
    },
    {
      name: 'bibleContent',
      validation: {
        level: 'error',
        arg: {$ref: ':bibleVersion'},
        messageKey: 'bibleVersion.missing',
      },
    },
    {
      name: 'agents',
      required: {
        should: {$not: isUnnamedAgentLink},
      },
      validation: [validateLinkedAgentId, validateExternalLinkedAgentId],
    },
    {
      name: 'educationLevel',
      required: 'should',
    },
    {
      name: 'grep',
      required: 'should',
    },
    {
      name: 'genreAndForm',
      required: {
        should: {
          $or: [
            {$not: {arg: {$ref: ':norwegianSubjects'}}},
            {
              $includes: {
                arg1: {$ref: ':literatureType'},
                arg2: CodeListRef.LITERATURE_TYPE['Skjønnlitteratur'],
              },
              default: false,
            },
          ],
        },
      },
    },
    {
      name: 'norwegianSubjects',
      required: {
        should: emptyGenreAndForm,
      },
    },
    {
      name: 'themes',
      required: 'should',
      listValidation: {
        ...workValidation_YcodeRequires5Acode,
        id: assert(workValidation_YcodeRequires5Acode.id),
        level: 'warning',
      },
    },
    {
      name: 'bookGroup',
      required: 'should',
    },
  ],
};
